import { useEffect, useState } from 'react';
import { adminApi, ensureArray } from '../services/api';
import { ConfirmDialog } from '../components/ConfirmDialog';

interface Affirmation {
  id: string;
  text: string;
  author: string | null;
  language: string;
  isActive: boolean;
}

interface BadgeDefinition {
  id: string;
  slug: string;
  title: string;
  description: string;
  missionType: string;
  targetCount: number;
  rewardLabel: string | null;
  isActive: boolean;
  sortOrder: number;
}

interface WellnessActivity {
  id: string;
  slug: string;
  title: string;
  description: string;
  category: string;
  durationMinutes: number;
  instructions: string;
  icon: string | null;
  isActive: boolean;
  sortOrder: number;
}

const ACTIVITY_CATEGORIES = [
  'meditation',
  'breathing',
  'mindfulness',
  'journaling_prompt',
] as const;

export function EngagementPage() {
  const [affirmations, setAffirmations] = useState<Affirmation[]>([]);
  const [badges, setBadges] = useState<BadgeDefinition[]>([]);
  const [activities, setActivities] = useState<WellnessActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [author, setAuthor] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [activityForm, setActivityForm] = useState({
    slug: '',
    title: '',
    description: '',
    category: 'meditation' as (typeof ACTIVITY_CATEGORIES)[number],
    durationMinutes: 5,
    instructions: '',
    icon: '',
  });
  const [savingActivity, setSavingActivity] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{
    kind: 'affirmation' | 'activity';
    id: string;
  } | null>(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      adminApi<Affirmation[]>('/admin/engagement/affirmations'),
      adminApi<BadgeDefinition[]>('/admin/engagement/badges'),
      adminApi<WellnessActivity[]>('/admin/engagement/activities'),
    ])
      .then(([a, b, acts]) => {
        setAffirmations(ensureArray(a));
        setBadges(ensureArray(b));
        setActivities(ensureArray(acts));
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const addAffirmation = async () => {
    if (!text.trim()) return;
    setSaving(true);
    setError('');
    try {
      await adminApi('/admin/engagement/affirmations', {
        method: 'POST',
        body: JSON.stringify({
          text: text.trim(),
          author: author.trim() || undefined,
          language: 'en',
        }),
      });
      setText('');
      setAuthor('');
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const toggleAffirmation = async (item: Affirmation) => {
    await adminApi(`/admin/engagement/affirmations/${item.id}`, {
      method: 'PUT',
      body: JSON.stringify({ isActive: !item.isActive }),
    });
    load();
  };

  const deleteAffirmation = (id: string) => {
    setDeleteTarget({ kind: 'affirmation', id });
  };

  const updateBadgeTarget = async (badge: BadgeDefinition, targetCount: number) => {
    await adminApi(`/admin/engagement/badges/${badge.id}`, {
      method: 'PUT',
      body: JSON.stringify({ targetCount }),
    });
    load();
  };

  const addActivity = async () => {
    if (!activityForm.slug.trim() || !activityForm.title.trim()) return;
    setSavingActivity(true);
    setError('');
    try {
      await adminApi('/admin/engagement/activities', {
        method: 'POST',
        body: JSON.stringify({
          slug: activityForm.slug.trim(),
          title: activityForm.title.trim(),
          description: activityForm.description.trim(),
          category: activityForm.category,
          durationMinutes: activityForm.durationMinutes,
          instructions: activityForm.instructions.trim(),
          icon: activityForm.icon.trim() || undefined,
        }),
      });
      setActivityForm({
        slug: '',
        title: '',
        description: '',
        category: 'meditation',
        durationMinutes: 5,
        instructions: '',
        icon: '',
      });
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSavingActivity(false);
    }
  };

  const toggleActivity = async (item: WellnessActivity) => {
    await adminApi(`/admin/engagement/activities/${item.id}`, {
      method: 'PUT',
      body: JSON.stringify({ isActive: !item.isActive }),
    });
    load();
  };

  const deleteActivity = (id: string) => {
    setDeleteTarget({ kind: 'activity', id });
  };

  return (
    <div className="page">
      <div className="page-title">
        <h1>Engagement</h1>
        <p className="mt-1 text-sm text-gray-500">
          Affirmations, badge missions, and wellness activities
        </p>
      </div>

      {error && <p className="alert-red mb-4">{error}</p>}

      <div className="card mb-6 space-y-3">
        <h3>Add affirmation</h3>
        <textarea
          className="input-field min-h-[80px]"
          placeholder="Affirmation text"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <input
          className="input-field"
          placeholder="Author (optional)"
          value={author}
          onChange={(e) => setAuthor(e.target.value)}
        />
        <button onClick={() => void addAffirmation()} disabled={saving} className="btn-primary">
          {saving ? 'Saving…' : 'Add affirmation'}
        </button>
      </div>

      <h3 className="section-title mb-3">Affirmations</h3>
      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : (
        <div className="menu-panel mb-8">
          {affirmations.map((item, index) => (
            <div
              key={item.id}
              className={`px-4 py-3 ${index < affirmations.length - 1 ? 'border-b border-white/60' : ''}`}
            >
              <p className="text-sm text-gray-900">{item.text}</p>
              {item.author && <p className="text-xs text-gray-500">— {item.author}</p>}
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  className="btn-ghost text-xs"
                  onClick={() => void toggleAffirmation(item)}
                >
                  {item.isActive ? 'Deactivate' : 'Activate'}
                </button>
                <button
                  type="button"
                  className="btn-danger text-xs"
                  onClick={() => void deleteAffirmation(item.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <h3 className="section-title mb-3">Badge definitions</h3>
      <div className="mb-8 space-y-3">
        {badges.map((badge) => (
          <div key={badge.id} className="card">
            <p className="font-semibold text-gray-900">{badge.title}</p>
            <p className="mt-1 text-xs text-gray-500">{badge.description}</p>
            <p className="mt-2 text-xs text-gray-600">
              Mission: {badge.missionType} · Target:
              <input
                type="number"
                min={1}
                className="input-compact ml-2 w-16"
                defaultValue={badge.targetCount}
                onBlur={(e) => {
                  const val = Number(e.target.value);
                  if (val > 0 && val !== badge.targetCount) {
                    void updateBadgeTarget(badge, val);
                  }
                }}
              />
            </p>
          </div>
        ))}
      </div>

      <div className="card mb-6 space-y-3">
        <h3>Add wellness activity</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <input
            className="input-field"
            placeholder="Slug (e.g. box-breathing)"
            value={activityForm.slug}
            onChange={(e) => setActivityForm((f) => ({ ...f, slug: e.target.value }))}
          />
          <input
            className="input-field"
            placeholder="Title"
            value={activityForm.title}
            onChange={(e) => setActivityForm((f) => ({ ...f, title: e.target.value }))}
          />
        </div>
        <textarea
          className="input-field min-h-[60px]"
          placeholder="Short description"
          value={activityForm.description}
          onChange={(e) => setActivityForm((f) => ({ ...f, description: e.target.value }))}
        />
        <textarea
          className="input-field min-h-[80px]"
          placeholder="Step-by-step instructions"
          value={activityForm.instructions}
          onChange={(e) => setActivityForm((f) => ({ ...f, instructions: e.target.value }))}
        />
        <div className="flex flex-wrap gap-3">
          <select
            className="input-field max-w-[200px]"
            value={activityForm.category}
            onChange={(e) =>
              setActivityForm((f) => ({
                ...f,
                category: e.target.value as (typeof ACTIVITY_CATEGORIES)[number],
              }))
            }
          >
            {ACTIVITY_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
          <input
            type="number"
            min={1}
            max={120}
            className="input-field w-28"
            placeholder="Minutes"
            value={activityForm.durationMinutes}
            onChange={(e) =>
              setActivityForm((f) => ({ ...f, durationMinutes: Number(e.target.value) }))
            }
          />
          <input
            className="input-field w-20"
            placeholder="Icon"
            value={activityForm.icon}
            onChange={(e) => setActivityForm((f) => ({ ...f, icon: e.target.value }))}
          />
        </div>
        <button
          onClick={() => void addActivity()}
          disabled={savingActivity}
          className="btn-primary"
        >
          {savingActivity ? 'Saving…' : 'Add activity'}
        </button>
      </div>

      <h3 className="section-title mb-3">Wellness activities</h3>
      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : (
        <div className="menu-panel space-y-0">
          {activities.map((item, index) => (
            <div
              key={item.id}
              className={`px-4 py-3 ${index < activities.length - 1 ? 'border-b border-white/60' : ''}`}
            >
              <p className="text-sm font-semibold text-gray-900">
                {item.icon ? `${item.icon} ` : ''}
                {item.title}
              </p>
              <p className="text-xs text-gray-500">
                {item.slug} · {item.durationMinutes} min · {item.category.replace(/_/g, ' ')}
                {!item.isActive && ' · inactive'}
              </p>
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  className="btn-ghost text-xs"
                  onClick={() => void toggleActivity(item)}
                >
                  {item.isActive ? 'Deactivate' : 'Activate'}
                </button>
                <button
                  type="button"
                  className="btn-danger text-xs"
                  onClick={() => void deleteActivity(item.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      <ConfirmDialog
        open={deleteTarget !== null}
        title={
          deleteTarget?.kind === 'activity' ? 'Delete wellness activity?' : 'Delete affirmation?'
        }
        message={
          deleteTarget?.kind === 'activity'
            ? 'Delete this wellness activity?'
            : 'Delete this affirmation?'
        }
        confirmLabel="Delete"
        danger
        onCancel={() => setDeleteTarget(null)}
        onConfirm={async () => {
          if (!deleteTarget) return;
          const path =
            deleteTarget.kind === 'activity'
              ? `/admin/engagement/activities/${deleteTarget.id}`
              : `/admin/engagement/affirmations/${deleteTarget.id}`;
          await adminApi(path, { method: 'DELETE' });
          setDeleteTarget(null);
          load();
        }}
      />
    </div>
  );
}
